/**
 * @file V2.5 升级弹窗（与 WebAgent 端同一份内容与状态）
 * @author sunyingying01
 */

import React, {useCallback, useEffect, useState} from 'react';
import {observer} from 'mobx-react-lite';
import {Checkbox, Input, Modal} from 'antd';
import type {CheckboxChangeEvent} from 'antd/es/checkbox';
import {LeftOutlined} from '@ant-design/icons';
import {useLocation} from 'react-router-dom';
import UpgradeNoticeHero from '@assets/upgradeNoticeHero.png';
import {UserStore} from '@/stores';
import {updateUserInfo} from '@/api/login';
import {messageManager} from '@/utils/errorHandler/MessageManager';
import styles from './index.module.less';

interface UpgradeNoticeModalProps {
    userStore: UserStore;
}

/** 普通用户公告是否弹过，与后端 user_config.user_check_states.check_states 同一份数据 */
export const UPGRADE_NOTICE_CHECK_KEY = 'upgrade_notice_v25_shown';

/** 等首屏渲染完再弹，避免盖在全屏 loading 上 */
const OPEN_DELAY_MS = 800;

const PHONE_PATTERN = /^1[3-9]\d{9}$/;

/** users/info 不走 common.Wrapper，成功 code 是 int 0，经 responseUnpackHook 归一后可能是 '0' */
function isSuccess(code?: string | number) {
    return code === 0 || code === '0';
}

/** 设计稿宽度：Pro 公告 654，普通公告与留手机号都是 500 */
const NOTICE_WIDTH = 654;
const NARROW_WIDTH = 500;

/**
 * 是否已经弹过，放在模块级而不是 useRef：
 * StrictMode 下 effect 会跑两遍，用 ref 在第一遍置位会导致 timer 被 cleanup 清掉后再也不弹
 */
let hasOpenedNotice = false;

/** 协议文案整段放在常量里，避免 JSX 折行在中文之间插入空格 */
const AGREEMENT_TEXT = '我已阅读并同意：平台原有效期内每日1,000积分将升级为每月3,000,000资源点，权益保持不变。点击「立即体验」即视为同意本协议并完成切换，切换后将保持新版体验。';

/** 一级：升级公告（Pro 才有协议确认与「暂不升级」入口） */
interface NoticeViewProps {
    isPro: boolean;
    agreed: boolean;
    confirming: boolean;
    onAgreedChange: (checked: boolean) => void;
    onExperience: () => void;
    onContact: () => void;
}

const NoticeView: React.FC<NoticeViewProps> = ({
    isPro,
    agreed,
    confirming,
    onAgreedChange,
    onExperience,
    onContact
}) => (
    <div className={styles.noticeWrapper}>
        <img src={UpgradeNoticeHero} alt="" className={styles.hero} />
        <div className={styles.notice}>
            <div className={styles.noticeTitle}>
                <span>百度伐谋 全新升级</span>
                <span className={styles.emoji}>🎉</span>
            </div>
            <p className={styles.noticeDesc}>
                全新产品体验与能力现已上线：
                <span className={styles.highlight}>「积分」</span>
                升级为
                <span className={styles.highlight}>「资源点」</span>
                ，支持更多复杂任务场景，现赠送 50,000 资源点，邀你体验～
            </p>
            {isPro && (
                <Checkbox
                    checked={agreed}
                    className={styles.agreement}
                    onChange={(event: CheckboxChangeEvent) => onAgreedChange(event.target.checked)}
                >
                    <span className={styles.agreementText}>{AGREEMENT_TEXT}</span>
                </Checkbox>
            )}
            <button
                type="button"
                className={styles.primaryBtn}
                disabled={isPro && (!agreed || confirming)}
                onClick={onExperience}
            >
                {isPro && confirming ? '提交中...' : '立即体验'}
            </button>
            {isPro && (
                <button type="button" className={styles.linkBtn} onClick={onContact}>
                    暂不升级，联系伐谋团队了解更多
                </button>
            )}
        </div>
    </div>
);

/** 二级：联系伐谋团队留手机号 */
interface ContactViewProps {
    phone: string;
    submitting: boolean;
    onPhoneChange: (phone: string) => void;
    onSubmit: () => void;
    onBack: () => void;
}

const ContactView: React.FC<ContactViewProps> = ({
    phone,
    submitting,
    onPhoneChange,
    onSubmit,
    onBack
}) => (
    <div className={styles.contact}>
        <button type="button" aria-label="返回升级弹窗" className={styles.backBtn} onClick={onBack}>
            <LeftOutlined />
        </button>
        <div className={styles.contactTitle}>联系伐谋团队</div>
        <p className={styles.contactDesc}>如需人工协助，请留下手机号，我们将在 2–3 个工作日内联系你</p>
        <label htmlFor="pro-upgrade-phone" className={styles.phoneLabel}>手机号</label>
        <Input
            id="pro-upgrade-phone"
            value={phone}
            maxLength={11}
            placeholder="请输入你的手机号"
            onChange={(event: React.ChangeEvent<HTMLInputElement>) => onPhoneChange(event.target.value.trim())}
            onPressEnter={onSubmit}
        />
        <button
            type="button"
            className={styles.primaryBtn}
            disabled={!PHONE_PATTERN.test(phone) || submitting}
            onClick={onSubmit}
        >
            {submitting ? '提交中...' : '提交'}
        </button>
    </div>
);

/**
 * V2.5 升级弹窗，按套餐分两种形态：
 * - 普通用户：单按钮公告，可关闭，展示即落库 check_states，每个账号只弹一次
 * - Pro 老用户：强制确认弹窗（无关闭入口），必须勾协议并点「立即体验」才提交 v25_agreed=true；
 *   点「暂不升级」进二级「联系伐谋团队」留手机号（v25_agreed=false + 来源 workspace），
 *   留了手机号也不算确认升级，下次刷新继续弹
 */
const UpgradeNoticeModal: React.FC<UpgradeNoticeModalProps> = observer(({userStore}) => {
    const {hasLogin, isPro, accountInfoLoaded, needV25UpgradeConfirm} = userStore;
    const {pathname} = useLocation();
    const hasShownNotice = Boolean(userStore.coldStartCheckStates[UPGRADE_NOTICE_CHECK_KEY]);
    const isEnterpriseAdminRoute = pathname === '/admin' || pathname.startsWith('/admin/');
    const [visible, setVisible] = useState(false);
    const [view, setView] = useState<'notice' | 'contact'>('notice');
    const [agreed, setAgreed] = useState(false);
    const [phone, setPhone] = useState('');
    const [confirming, setConfirming] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(
        () => {
            if (isEnterpriseAdminRoute) {
                return undefined;
            }

            // 套餐信息未就绪时不能判断形态，否则 Pro 用户会先看到普通公告并被落库
            if (hasOpenedNotice || !hasLogin || !accountInfoLoaded) {
                return undefined;
            }

            if (isPro ? !needV25UpgradeConfirm : hasShownNotice) {
                return undefined;
            }

            const timer = setTimeout(
                () => {
                    if (hasOpenedNotice) {
                        return;
                    }
                    hasOpenedNotice = true;
                    setVisible(true);
                    // 普通公告展示即落库；Pro 必须主动确认升级，这里不提交
                    if (!isPro) {
                        userStore.markCheckState(UPGRADE_NOTICE_CHECK_KEY);
                    }
                },
                OPEN_DELAY_MS
            );
            return () => clearTimeout(timer);
        },
        [hasLogin, accountInfoLoaded, isPro, needV25UpgradeConfirm, hasShownNotice,
            isEnterpriseAdminRoute, userStore]
    );

    const showContactView = useCallback(
        () => setView('contact'),
        []
    );
    const showNoticeView = useCallback(
        () => setView('notice'),
        []
    );
    const closeModal = useCallback(
        () => setVisible(false),
        []
    );

    const handleExperience = useCallback(
        async () => {
            // 普通用户没有确认动作，点「立即体验」直接关闭
            if (!isPro) {
                setVisible(false);
                return;
            }

            if (!agreed || confirming) {
                return;
            }

            setConfirming(true);
            try {
                // v25_agreed=true 即升级确认，Workspace / WebAgent 共用这一份状态
                const res = await updateUserInfo({v25_agreed: true});
                if (!isSuccess(res.code)) {
                    throw new Error(res.message || '升级失败，请重试');
                }
                userStore.setWaUserInfo(res.data);
                setVisible(false);
            } catch (error: any) {
                messageManager.showError(error?.message || '升级失败，请重试');
            } finally {
                setConfirming(false);
            }
        },
        [agreed, confirming, isPro, userStore]
    );

    const handleSubmitPhone = useCallback(
        async () => {
            if (!PHONE_PATTERN.test(phone) || submitting) {
                return;
            }

            setSubmitting(true);
            try {
                // 手机号格式由前端校验；来源按当前平台传 workspace
                const res = await updateUserInfo({
                    phone,
                    v25_agreed: false,
                    v25_contact_source: 'workspace'
                });
                if (!isSuccess(res.code)) {
                    throw new Error(res.message || '提交失败，请重试');
                }
                userStore.setWaUserInfo(res.data);
                messageManager.showSuccess('提交成功，我们将在 2–3 个工作日内联系你。');
                setPhone('');
                // 留手机号不等于同意升级，回到升级弹窗，用户仍需确认后才能继续使用
                setView('notice');
            } catch (error: any) {
                messageManager.showError(error?.message || '提交失败，请重试');
            } finally {
                setSubmitting(false);
            }
        },
        [phone, submitting, userStore]
    );

    // 弹过一次后保留挂载，关闭动画才能正常播放
    if (isEnterpriseAdminRoute || (!hasOpenedNotice && !visible)) {
        return null;
    }

    const isContactView = isPro && view === 'contact';
    const modalWidth = isPro && !isContactView ? NOTICE_WIDTH : NARROW_WIDTH;

    return (
        <Modal
            open={visible}
            width={modalWidth}
            centered
            footer={null}
            closable={!isPro}
            maskClosable={false}
            keyboard={!isPro}
            onCancel={isPro ? undefined : closeModal}
            rootClassName={styles.upgradeNoticeModal}
        >
            {isContactView ? (
                <ContactView
                    phone={phone}
                    submitting={submitting}
                    onPhoneChange={setPhone}
                    onSubmit={handleSubmitPhone}
                    onBack={showNoticeView}
                />
            ) : (
                <NoticeView
                    isPro={isPro}
                    agreed={agreed}
                    confirming={confirming}
                    onAgreedChange={setAgreed}
                    onExperience={handleExperience}
                    onContact={showContactView}
                />
            )}
        </Modal>
    );
});

export default UpgradeNoticeModal;
